import { analytics, identify, reset } from './utils';
import { cartViewed, checkoutCompleted, checkoutStarted, collectionClicked, couponApplied, couponDenied, couponEntered, couponRemoved, emailCaptured, loginStarted, orderCompleted, productAdded, productClicked, productRemoved, productViewed, signedIn, signedOut, stockRequestCreated, subscriptionCancelled, subscriptionOrderSkipped, subscriptionPaused, subscriptionResumed, variantClicked, } from './typewriter';
import { serializeCartViewed, serializeCheckoutCompleted, serializeCheckoutStarted, serializeCollectionClicked, serializeCouponItem, serializeEmailCaptured, serializeMeta, serializeOrderCompleted, serializeProductAdded, serializeProductClicked, serializeProductRemoved, serializeProductViewed, serializeStockRequestCreated, serializeSubscription, serializeVariantClicked, serializeUnpurchasedSubscription, serializePrepaidSubscription, } from './serialize';
import { createAnalyticsConfig } from './config';
const ANALYTICS_TIMEOUT = 2500;
/**
 * Client for interacting with Segment Analytics.
 *
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/sources/website/analytics.js/quickstart/
 */
export const createAnalyticsClient = (analyticsClientConfig) => {
    const config = createAnalyticsConfig(analyticsClientConfig);
    const meta = serializeMeta(config);
    if (typeof window !== 'undefined') {
        setTimeout(() => {
            if (!analytics()) {
                const message = 'window.analytics is undefined. Segment is not installed correctly, so Chord cannot send events to Segment. See https://docs.chord.co/adding-tracking for troubleshooting.';
                // eslint-disable-next-line no-console
                console[process.env.NODE_ENV !== 'production' ? 'error' : 'warn'](message);
            }
        }, ANALYTICS_TIMEOUT);
    }
    return {
        identify: (userId, traits, options, callback) => {
            identify(userId, { ...traits, ...meta }, options, callback);
        },
        page: (url, properties = {}) => {
            analytics()?.page(url, { ...meta, ...properties });
        },
        trackCartViewed: (props, ...rest) => {
            const serialized = serializeCartViewed(config, props);
            cartViewed(serialized, ...rest);
        },
        trackCheckoutCompleted: (props, ...rest) => {
            const userId = props.order?.userId?.toString() || '';
            const email = props.order?.email;
            identify(userId, { email });
            const serialized = serializeCheckoutCompleted(config, props);
            checkoutCompleted(serialized, ...rest);
        },
        trackCheckoutStarted: (props, ...rest) => {
            const serialized = serializeCheckoutStarted(config, props);
            checkoutStarted(serialized, ...rest);
        },
        trackCollectionClicked: (props, ...rest) => {
            const serialized = serializeCollectionClicked(config, props);
            collectionClicked(serialized, ...rest);
        },
        trackCouponApplied: (props, ...rest) => {
            const serialized = serializeCouponItem(config, props);
            couponApplied(serialized, ...rest);
        },
        trackCouponDenied: (props, ...rest) => {
            const serialized = {
                ...serializeCouponItem(config, props),
                reason: props.reason,
            };
            couponDenied(serialized, ...rest);
        },
        trackCouponEntered: (props, ...rest) => {
            const serialized = serializeCouponItem(config, props);
            couponEntered(serialized, ...rest);
        },
        trackCouponRemoved: (props, ...rest) => {
            const serialized = serializeCouponItem(config, props);
            couponRemoved(serialized, ...rest);
        },
        trackEmailCaptured: (props, ...rest) => {
            identify('', {
                email: props.emailCapture.email,
            });
            const serialized = serializeEmailCaptured(config, props);
            emailCaptured(serialized, ...rest);
        },
        trackLoginStarted: (props, ...rest) => {
            if (props?.email) {
                identify('', { email: props.email });
            }
            loginStarted({ ...props, ...meta }, ...rest);
        },
        trackOrderCompleted: (props, ...rest) => {
            const serialized = serializeOrderCompleted(config, props);
            orderCompleted(serialized, ...rest);
        },
        trackProductAdded: (props, ...rest) => {
            const serialized = serializeProductAdded(config, props);
            productAdded(serialized, ...rest);
        },
        trackProductClicked: (props, ...rest) => {
            const serialized = serializeProductClicked(config, props);
            productClicked(serialized, ...rest);
        },
        trackProductRemoved: (props, ...rest) => {
            const serialized = serializeProductRemoved(config, props);
            productRemoved(serialized, ...rest);
        },
        trackProductViewed: (props, ...rest) => {
            const serialized = serializeProductViewed(config, props);
            productViewed(serialized, ...rest);
        },
        trackSignedIn: (props, ...rest) => {
            identify(props.userId, { email: props.email });
            signedIn({ ...props, ...meta }, ...rest);
        },
        trackSignedOut: (props, options, callback) => {
            signedOut({ ...props, ...meta }, options, () => {
                reset();
                if (callback && typeof callback === 'function') {
                    callback();
                }
            });
        },
        trackStockRequestCreated: (props, ...rest) => {
            identify('', { email: props.email });
            const serialized = serializeStockRequestCreated(config, props);
            stockRequestCreated(serialized, ...rest);
        },
        trackSubscriptionAdded: (props, ...rest) => {
            const serialized = serializeUnpurchasedSubscription(config, props);
            productAdded(serialized, ...rest);
        },
        trackPrepaidSubscriptionAdded: (props, ...rest) => {
            const serialized = serializePrepaidSubscription(config, props);
            productAdded(serialized, ...rest);
        },
        trackSubscriptionCancelled: (props, ...rest) => {
            const serialized = serializeSubscription(config, props);
            subscriptionCancelled(serialized, ...rest);
        },
        trackSubscriptionPaused: (props, ...rest) => {
            const serialized = serializeSubscription(config, props);
            subscriptionPaused(serialized, ...rest);
        },
        trackSubscriptionResumed: (props, ...rest) => {
            const serialized = serializeSubscription(config, props);
            subscriptionResumed(serialized, ...rest);
        },
        trackSubscriptionSkipped: (props, ...rest) => {
            const serialized = serializeSubscription(config, props);
            subscriptionOrderSkipped(serialized, ...rest);
        },
        trackVariantClicked: (props, ...rest) => {
            const serialized = serializeVariantClicked(config, props);
            variantClicked(serialized, ...rest);
        },
    };
};
